<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0 text-capitalize">Loading Funnel</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item">
                <router-link to="/">Home</router-link>
              </li>
              <li class="breadcrumb-item">Advanced Reports</li>
              <li class="breadcrumb-item active text-capitalize">
                <i class="fas fa-filter"></i> Loading Funnel
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <data-table-wrapper
            ref="dataTable"
            :columns="columns"
            :filters="filters"
            :load-data-callback="loadDataCallback"
            :filter-defaults="filterDefaults"
            :show-filters="true"
            disable-primary-column="true"
            disable-edit-action="true"
            disable-delete-action="true"
            :disable-auto-load="true"
            endpoint="/telemetry-funnel-report"
            api-service="analytics"
        >
        </data-table-wrapper>
      </div>
    </section>
  </div>
</template>

<script>
import DataTableWrapper from '../components/DataTableWrapper';
import {mapGetters} from "vuex";
import * as dateUtils from "@/helpers/dateUtils";

const GROUP_BY_TOTAL = 'total';
const GROUP_BY_GAME = 'game';
const GROUP_BY_OPERATOR = 'operator';

const SEGMENT_TITLES = {
  all: 'All devices',
  desktop: 'Desktop',
  mobile_portrait: 'Mobile portrait',
  mobile_landscape: 'Mobile landscape',
  other: 'Other',
};

const SESSION_STEPS = [
  {field: 'loaded', title: 'Loaded'},
  {field: 'sound_popup_shown', title: 'Sound popup'},
  {field: 'slot_displayed', title: 'Slot displayed'},
  {field: 'balance_sufficient', title: 'Enough balance'},
  {field: 'balance_insufficient', title: 'Not enough balance'},
  {field: 'with_spin', title: 'With spin'},
  {field: 'without_spin', title: 'Without spin'},
  {field: 'menu_open', title: 'Menu'},
  {field: 'rules_open', title: 'Rules'},
  {field: 'profile_open', title: 'Profile'},
  {field: 'mode_select', title: 'Mode select'},
  {field: 'bet_change', title: 'Bet change'},
];

const PLAYER_STEPS = [
  {field: 'players_with_spin', title: 'Players with spin'},
  {field: 'players_without_spin', title: 'Players without spin'},
];

export default {
  components: {
    DataTableWrapper
  },
  data() {
    return {
      loadedGroupBy: GROUP_BY_TOTAL,
      loadDataCallback: (component, respData) => {
        this.loadedGroupBy = component.getFilterValue('groupBy') || GROUP_BY_TOTAL;
        component.items = respData;
        component.pagination.onPage = component.pagination.total = component.items.length;
        component.pagination.page = 1;
        component.showEmpty = true;
      },
      filters: [
        {
          field: 'fromDate',
          placeholder: 'From Date',
          label: 'From Date',
          type: 'date-picker',
          shortcuts: dateUtils.getFilterShortcuts()
        },
        {
          field: 'toDate',
          placeholder: 'To Date',
          label: 'To Date',
          type: 'date-picker',
          shortcuts: dateUtils.getFilterShortcuts()
        },
        {
          field: 'gameIds',
          placeholder: 'Select games',
          label: 'Games',
          type: 'multiselect',
          optionLabel: 'name',
          multiple: true,
          getValue: (games) => { return games ? games.map(game => game.id) : [] },
          getOptions: () => this.getGames,
          trackBy: 'id'
        },
        {
          field: 'operatorIds',
          placeholder: 'Select operators',
          label: 'Operators',
          type: 'multiselect',
          optionLabel: 'name',
          multiple: true,
          getValue: (operators) => { return operators ? operators.map(operator => operator.id) : [] },
          getOptions: () => this.getOperators,
          trackBy: 'id'
        },
        {
          field: 'groupBy',
          label: 'Group by',
          type: 'btn-group',
          buttons: [
            {value: GROUP_BY_TOTAL, label: 'Total'},
            {value: GROUP_BY_GAME, label: 'Games'},
            {value: GROUP_BY_OPERATOR, label: 'Operators'},
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'getGames',
      'getGame',
      'getOperators',
      'getOperator',
    ]),
    filterDefaults() {
      return {
        fromDate: dateUtils.getTodayDate(),
        toDate: dateUtils.getTodayEndDate(),
        groupBy: GROUP_BY_TOTAL,
      };
    },
    columns() {
      return [
        {
          field: 'group_id',
          title: 'Group',
          sorting: false,
          callback: ({row}) => this.groupTitle(row.group_id),
        },
        {
          field: 'segment',
          title: 'Device',
          sorting: false,
          callback: ({row}) => SEGMENT_TITLES[row.segment] || row.segment,
        },
        {
          field: 'sessions',
          title: 'Load started',
          sorting: false,
        },
        ...SESSION_STEPS.map(step => this.stepColumn(step)),
        {
          field: 'players',
          title: 'Players',
          sorting: false,
        },
        ...PLAYER_STEPS.map(step => this.stepColumn(step)),
      ];
    },
  },
  methods: {
    stepColumn({field, title}) {
      return {
        field,
        title,
        sorting: false,
        callback: ({row}) => {
          const percent = row[field + '_percent'];
          return percent != null ? `${row[field]} (${percent}%)` : row[field];
        },
      };
    },
    groupTitle(groupId) {
      if (this.loadedGroupBy === GROUP_BY_GAME) {
        const game = this.getGame(groupId) || this.getGame(String(groupId));
        return game ? game.name : groupId;
      }
      if (this.loadedGroupBy === GROUP_BY_OPERATOR) {
        const operator = this.getOperator(groupId) || this.getOperator(String(groupId));
        return operator ? operator.name : groupId;
      }
      return 'Total';
    },
  },
}
</script>
